import { isSameDay, isValid, parse } from "date-fns";
import React, { useEffect, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";

import { ButtonStandard, InputWrapper, SingleSelectInput, Text } from "@performyard/py-components";

import DatePicker from "../../../../../components/Inputs/DatePicker";
import Input from "../../../../../components/Inputs/InputText";
import InputTextArea from "../../../../../components/Inputs/InputTextArea";
import { Checkboxes } from "../../../../../components/Inputs/SelectionOptions/Checkboxes";
import ContentModal from "../../../../../components/Modal/Templates/ContentModal";

import { useLatestMeetingCarryOverItems } from "../../../../../api/hooks";
import { useCalendarIntegrationStatus } from "../../hooks";

import { config } from "../../config";
import type { FormErrors, MeetingFormSchema } from "./meeting-form-schema";
import {
	type MeetingFormValuesWithOptionalDates,
	generateTimeOptions,
	handleEndDateChange,
	handleStartDateChange,
	handleStartTimeChange,
} from "./meeting-form-utils";

export type MeetingFormData = {
	name: string;
	description: string;
	startDateTime: Date;
	endDateTime: Date;
	carryOver: boolean;
	addToCalendar: boolean;
};

type MeetingFormProps = {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: MeetingFormData) => void;
	formValues: MeetingFormValuesWithOptionalDates;
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void;
	validationErrors: FormErrors;
	submitLabel: string;
	isPending: boolean;
	title: string;
	showCarryOver?: boolean;
	showCalendarIntegration?: boolean;
	workspaceId?: string;
	calendarUpdateNotification?: string;
};

const MeetingForm = ({
	isOpen,
	onClose,
	onSubmit,
	formValues,
	onFormChange,
	validationErrors,
	submitLabel,
	isPending,
	title,
	showCarryOver = false,
	showCalendarIntegration = true,
	workspaceId,
	calendarUpdateNotification,
}: MeetingFormProps): React.ReactElement => {
	const { isCalendarIntegrationEnabled } = useCalendarIntegrationStatus();

	const startDateTime = useMemo(() => {
		if (!formValues.startDate || !isValid(formValues.startDate)) {
			return new Date();
		}
		return parse(formValues.startTime, "HH:mm", formValues.startDate);
	}, [formValues.startTime, formValues.startDate]);

	const [debouncedStartDate] = useDebounceValue(startDateTime, 100);

	const { data: latestMeetingCarryOverItems, isLoading: isLatestMeetingCarryOverItemsLoading } =
		useLatestMeetingCarryOverItems(workspaceId ?? "", debouncedStartDate, showCarryOver && !!workspaceId);

	const carryOverItemsCount = latestMeetingCarryOverItems?.count ?? 0;

	useEffect(() => {
		if (carryOverItemsCount === 0 && formValues.carryOver) {
			onFormChange({ carryOver: false });
		}
	}, [carryOverItemsCount, formValues.carryOver, onFormChange]);

	const carryOverItemsLabel = `Carryover ${carryOverItemsCount} unchecked item${carryOverItemsCount === 1 ? "" : "s"} from the previous meeting`;
	const carryOverItemsInputDisabled = isPending || isLatestMeetingCarryOverItemsLoading || carryOverItemsCount === 0;

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		// If dates are not set, validation will fail in the parent component
		if (!formValues.startDate || !formValues.endDate) {
			onSubmit({
				name: formValues.name.trim(),
				description: formValues.description?.trim() || "",
				startDateTime: new Date(),
				endDateTime: new Date(),
				carryOver: formValues.carryOver,
				addToCalendar: formValues.addToCalendar,
			});
			return;
		}

		const startDT = parse(formValues.startTime, "HH:mm", formValues.startDate);
		const endDT = parse(formValues.endTime, "HH:mm", formValues.endDate);

		const data = {
			name: formValues.name.trim(),
			description: formValues.description?.trim() || "",
			startDateTime: startDT,
			endDateTime: endDT,
			carryOver: formValues.carryOver,
			addToCalendar: formValues.addToCalendar,
		};

		onSubmit(data);
	};

	const timeOptions = useMemo(() => generateTimeOptions(formValues.startDate ?? new Date()), [formValues.startDate]);

	const availableStartTimeOptions = timeOptions;

	const availableEndTimeOptions = useMemo(() => {
		const endTimeOptions = generateTimeOptions(formValues.endDate ?? new Date());

		// Only filter time options if both dates are valid and on the same day
		if (
			formValues.startDate &&
			formValues.endDate &&
			isValid(formValues.startDate) &&
			isValid(formValues.endDate) &&
			isSameDay(formValues.startDate, formValues.endDate)
		) {
			return endTimeOptions.filter((option) => option.value > formValues.startTime);
		}
		return endTimeOptions;
	}, [formValues.startDate, formValues.endDate, formValues.startTime]);

	return (
		<ContentModal
			isOpen={isOpen}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
			}
			subTitle={
				calendarUpdateNotification && (
					<div className="mt-4 text-center">
						<Text size="paragraph-3" weight="regular">
							{calendarUpdateNotification}
						</Text>
					</div>
				)
			}
			handleClose={onClose}
			buttons={[
				<div key="cancelButton">
					<ButtonStandard size="large" onClick={onClose} disabled={isPending}>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="submitButton">
					<ButtonStandard
						size="large"
						color="green"
						type="submit"
						form="meeting-form"
						disabled={isPending || isLatestMeetingCarryOverItemsLoading}
						data-testid="meeting-form-submit-button"
					>
						{submitLabel}
					</ButtonStandard>
				</div>,
			]}
		>
			<form id="meeting-form" data-testid="meeting-form" onSubmit={handleSubmit}>
				<Input
					placeholder="Meeting name here"
					value={formValues.name}
					onChange={(val) => onFormChange({ name: val })}
					disabled={isPending}
					clearable={true}
					label="Meeting Name"
					labelSize="paragraph-3"
					maxLength={280}
					errorMessage={
						validationErrors.name
							? validationErrors.name
							: formValues.name.length > 280
								? "Name cannot exceed 280 characters"
								: ""
					}
					animatedError
					data-testid="meeting-name-input"
				/>
				<div className="h-4" />
				<div className="flex flex-row gap-4">
					<InputWrapper id="start-date-input" label="Start Date" error={validationErrors.startDate}>
						<div style={{ width: config.layout.datePickerWidth }}>
							<DatePicker
								value={formValues.startDate ?? ""}
								onChange={(val) => handleStartDateChange(val, formValues, onFormChange)}
								disabled={isPending}
								hasIcon={true}
								clearable={true}
							/>
						</div>
					</InputWrapper>
					<InputWrapper id="end-date-input" label="End Date" error={validationErrors.endDate}>
						<div style={{ width: config.layout.datePickerWidth }}>
							<DatePicker
								value={formValues.endDate ?? ""}
								onChange={(val) => handleEndDateChange(val, formValues, onFormChange)}
								disabled={isPending}
								hasIcon={true}
								clearable={true}
							/>
						</div>
					</InputWrapper>
				</div>
				<div className="h-4" />
				<div className="grid grid-flow-col grid-cols-2 gap-4">
					<div className="col-span-1" style={{ width: config.layout.datePickerWidth }}>
						<InputWrapper id="start-time-input" label="Start Time" error={validationErrors.startTime}>
							<SingleSelectInput<string>
								options={availableStartTimeOptions}
								value={formValues.startTime}
								onChange={(option) =>
									handleStartTimeChange({ value: option ?? "" }, formValues, onFormChange)
								}
								placeholder="Select a start time"
								disabled={isPending}
								data-testid="start-time-select"
							/>
						</InputWrapper>
					</div>
					<div className="col-span-1" style={{ width: config.layout.datePickerWidth }}>
						<InputWrapper id="end-time-input" label="End Time" error={validationErrors.endTime}>
							<SingleSelectInput<string>
								options={availableEndTimeOptions}
								value={formValues.endTime}
								onChange={(option) => {
									onFormChange({ endTime: option ?? "" });
								}}
								placeholder="Select an end time"
								disabled={isPending}
								data-testid="end-time-select"
							/>
						</InputWrapper>
					</div>
				</div>
				<div className="h-4" />
				<InputTextArea
					value={formValues.description || ""}
					onChange={(val) => onFormChange({ description: val })}
					placeholder="Your meeting description and link"
					minRows={6}
					maxRows={6}
					disabled={isPending}
					label="Description"
					labelSize="paragraph-3"
					errorMessage={
						validationErrors.description
							? validationErrors.description
							: (formValues.description?.length || 0) > config.meetingDescriptionMaxLength
								? `Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
								: ""
					}
					data-testid="meeting-description-input"
				/>
				{showCarryOver && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: "carryOver", label: carryOverItemsLabel }]}
							value={formValues.carryOver ? [{ value: "carryOver", label: carryOverItemsLabel }] : []}
							disabled={carryOverItemsInputDisabled ? ["carryOver"] : []}
							onChange={() => onFormChange({ carryOver: !formValues.carryOver })}
						/>
					</>
				)}
				{showCalendarIntegration && isCalendarIntegrationEnabled && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: "addToCalendar", label: "Add Meeting to My Calendar" }]}
							value={
								formValues.addToCalendar
									? [{ value: "addToCalendar", label: "Add Meeting to My Calendar" }]
									: []
							}
							disabled={isPending ? ["addToCalendar"] : []}
							onChange={() => onFormChange({ addToCalendar: !formValues.addToCalendar })}
						/>
					</>
				)}
			</form>
		</ContentModal>
	);
};

export default MeetingForm;
